// src/components/DDTTab.js
import React, { useState, useEffect } from "react";
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc,
  doc, serverTimestamp, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { prossimoProgressivo } from "../utils/contatoriUtils";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../utils/Toast";
import { confirm } from "../utils/confirm";
import { Plus, Trash2, Printer, X, Copy, FileText } from "lucide-react";
import { toLocalDateStr } from "../utils/dateUtils";
import { SelettoreSede } from "./SediCliente";
import { copiaLibera, descrizioneSede } from "../utils/sediCliente";
import { apriDocumento, testataHtml, piedeHtml, esc, dataLunga } from "../utils/cartaIntestata";

const CAUSALI = [
  "Vendita","Conto lavorazione","Conto visione","Reso","Riparazione",
  "Trasferimento","Comodato","Sostituzione","Consegna in cantiere","Altro"
];
const STATI_DDT = {
  bozza:     { label: "Bozza",     bg: "#f3f4f6", col: "#6b7280" },
  emesso:    { label: "Emesso",    bg: "#d1fae5", col: "#065f46" },
  annullato: { label: "Annullato", bg: "#fee2e2", col: "#991b1b" },
};
const UM_OPTIONS = ["pz","kg","mt","ml","mq","mc","lt","set","conf","h","altro"];

// Sez definito FUORI dal componente per evitare re-creazione ad ogni render (bug focus)
const Sez = ({title, emoji, colore, sfondo, children}) => (
  <div style={{border:"1px solid #e5e7eb",borderRadius:12,overflow:"hidden",marginBottom:16}}>
    <div style={{background:sfondo||"#f9fafb",padding:"10px 16px",display:"flex",alignItems:"center",gap:8,borderBottom:"1px solid #e5e7eb"}}>
      <span style={{fontSize:16}}>{emoji}</span>
      <span style={{fontSize:13,fontWeight:700,color:colore||"#1e1b4b"}}>{title}</span>
    </div>
    <div style={{padding:16,background:"#fff"}}>{children}</div>
  </div>
);

function DDTForm({ ddt, projectId, projectName, codiceCommessa, clienteNome, clienteId, orgId, orgInfo, onClose, commessaChiusa=false }) {
  // Sede dei lavori della commessa: è la destinazione proposta per i DDT nuovi
  const [sedeCommessa, setSedeCommessa] = useState(null);
  useEffect(() => { if (projectId && !ddt) getDoc(doc(db, "projects", projectId)).then(s => setSedeCommessa(s.exists() ? (s.data().sede || null) : null)).catch(() => {}); }, [projectId, ddt]);
  const { currentUser } = useAuth();
  const { addToast } = useToast();
  const oggi = toLocalDateStr(new Date());
  const oraOra = new Date().toTimeString().slice(0,5);

  const [form, setForm] = useState(ddt || {
    clienteId: clienteId||"", destinatarioNome: clienteNome||"",
    destinatarioPiva:"", destinatarioCF:"", destinatarioIndirizzo:"",
    tipoDestinazione:"cliente", destinazioneManuale:"",
    dataDocumento: oggi, causale:"Consegna in cantiere", causalePersonalizzata:"",
    trasportoCura:"Mittente", vettoreNome:"", vettoreIndirizzo:"",
    dataInizioTrasporto: oggi, oraInizioTrasporto: oraOra,
    nColli:"", peso:"", aspettoEsteriore:"Involto", notesTrasporto:"",
    stato:"bozza", note:"",
  });
  const [righe, setRighe] = useState(ddt?.righe || [
    { codice:"", descrizione:"", quantita:"", um:"pz", note:"" }
  ]);
  const [clienti, setClienti] = useState([]);
  const [saving, setSaving] = useState(false);
  const upd = (k,v) => setForm(f=>({...f,[k]:v}));

  useEffect(()=>{
    if(!orgId) return;
    onSnapshot(query(collection(db,"clienti"),where("organizationId","==",orgId)),
      snap=>setClienti(snap.docs.map(d=>({id:d.id,...d.data()}))));
  },[orgId]);

  useEffect(()=>{
    if(!form.clienteId) return;
    const cl=clienti.find(c=>c.id===form.clienteId);
    if(!cl) return;
    upd("destinatarioNome", cl.ragioneSociale||"");
    upd("destinatarioPiva", cl.piva||"");
    upd("destinatarioCF", cl.codiceFiscale||"");
    upd("destinatarioIndirizzo", cl.indirizzo||"");
  },[form.clienteId, clienti]);

  const addRiga = () => setRighe(r=>[...r,{codice:"",descrizione:"",quantita:"",um:"pz",note:""}]);
  const updRiga = (i,k,v) => setRighe(r=>r.map((x,j)=>j===i?{...x,[k]:v}:x));
  const removeRiga = i => setRighe(r=>r.filter((_,j)=>j!==i));
  const duplicaRiga = i => setRighe(r=>[...r.slice(0,i+1),{...r[i]},...r.slice(i+1)]);

  const destinazioneEffettiva = () => {
    // Da ottobre 2026 la destinazione si sceglie tra le sedi del cliente (o si scrive a mano)
    if(form.tipoDestinazione==="sede") return descrizioneSede(form.sedeDestinazione) || form.destinatarioIndirizzo;
    if(form.tipoDestinazione==="manuale") return form.destinazioneManuale;
    if(form.tipoDestinazione==="commessa") return `${projectName||""} — ${codiceCommessa||""}`;
    return form.destinatarioIndirizzo;
  };

  // Numerazione (ottobre 2026): il numero DDT-AAAA-NNNN si assegna all'EMISSIONE, con il
  // contatore progressivo dell'azienda (transazione: mai due DDT con lo stesso numero). Le
  // bozze non consumano numeri: eliminarne una non lascia buchi nella numerazione. I DDT già
  // numerati in bozza prima di questa modifica mantengono il loro numero.
  const salva = async(emetti=false) => {
    if(commessaChiusa || saving) return;
    if(!form.destinatarioNome.trim()){addToast("Inserisci il destinatario","error");return;}
    if(emetti && !righe.some(r=>String(r.descrizione||"").trim())){addToast("Indica almeno un bene trasportato prima di emettere il DDT","error");return;}
    if(emetti && !(await confirm("Emettere il DDT? Riceve il numero progressivo e non sarà più modificabile (si potrà solo annullare).",{confirmLabel:"Emetti DDT"}))) return;
    setSaving(true);
    try {
      const dati={
        ...form, righe,
        projectId:projectId||null, projectName:projectName||"",
        codiceCommessa:codiceCommessa||"", orgId,
        orgNome:orgInfo?.nome||orgInfo?.name||"",
        destinazioneEffettiva:destinazioneEffettiva(),
        compilatoreUid:form.compilatoreUid||currentUser?.uid,
        compilatoreNome:form.compilatoreNome||currentUser?.displayName||"",
        stato:emetti?"emesso":form.stato,
        updatedAt:serverTimestamp(),
      };
      delete dati.id;
      let numero = ddt?.numero || "";
      if(emetti && !numero){
        const anno = Number(String(form.dataDocumento||"").slice(0,4)) || new Date().getFullYear();
        const prog = await prossimoProgressivo(`${orgId}_ddt_${anno}`, { orgId, anno });
        numero = `DDT-${anno}-${String(prog).padStart(4,"0")}`;
        dati.numero = numero;
      }
      if(emetti){
        dati.dataEmissione=new Date().toISOString();
        dati.emessoNome=currentUser?.displayName||"";
      }
      if(ddt?.id){
        await updateDoc(doc(db,"ddt",ddt.id),dati);
      } else {
        await addDoc(collection(db,"ddt"),{...dati,createdAt:serverTimestamp()});
      }
      addToast(emetti?`DDT ${numero} emesso ✅`:(ddt?.id?"Bozza aggiornata ✅":"Bozza salvata: il numero si assegna all'emissione"),"success");
      onClose();
    } catch(err) {
      addToast("DDT non salvato: "+(err?.message||"errore"),"error");
    } finally { setSaving(false); }
  };

  const annullaDDT = async()=>{
    if(commessaChiusa || !ddt?.id) return;
    if(!await confirm("Annullare questo DDT? L'operazione non può essere annullata.",{type:"danger"})) return;
    await updateDoc(doc(db,"ddt",ddt.id),{stato:"annullato",annullatoNome:currentUser?.displayName,updatedAt:serverTimestamp()});
    addToast("DDT annullato","info");
    onClose();
  };

  const stampaPDF=()=>{
    const stato=STATI_DDT[form.stato]||STATI_DDT.bozza;
    const righeValide=righe.filter(r=>String(r.descrizione||"").trim());
    const righeHtml=righeValide.map((r,i)=>`<tr><td class="c">${i+1}</td><td>${esc(r.codice)}</td><td>${esc(r.descrizione)}</td><td class="n">${esc(r.quantita)}</td><td>${esc(r.um)}</td><td class="nt">${esc(r.note)}</td></tr>`).join("")
      + Array.from({length:Math.max(0,6-righeValide.length)},()=>`<tr class="vuota"><td></td><td></td><td></td><td></td><td></td><td></td></tr>`).join("");
    const dest=destinazioneEffettiva();
    const causale=form.causale==="Altro"?form.causalePersonalizzata:form.causale;
    const numero=ddt?.numero||"";
    const css=`
      .riquadri{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:12px;}
      .box{border:1px solid #d1d5db;border-radius:6px;padding:9px 12px;}
      .box-t{font-size:8.5px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:.05em;margin-bottom:4px;}
      .box h3{font-size:13px;font-weight:800;color:#111827;margin-bottom:2px;}
      .box p{font-size:10.5px;color:#374151;}
      .trasporto{display:grid;grid-template-columns:repeat(5,1fr);border:1px solid #d1d5db;border-radius:6px;margin-bottom:12px;overflow:hidden;}
      .trasporto div{padding:7px 10px;border-right:1px solid #e5e7eb;}
      .trasporto div:last-child{border-right:none;}
      .t-lbl{font-size:8px;color:#6b7280;font-weight:700;text-transform:uppercase;display:block;margin-bottom:2px;}
      .t-val{font-size:11px;font-weight:700;}
      table{width:100%;border-collapse:collapse;margin-bottom:12px;}
      th{background:#1e1b4b;color:#fff;padding:6px 8px;font-size:9px;font-weight:700;text-align:left;text-transform:uppercase;}
      td{padding:6px 8px;border:1px solid #e5e7eb;font-size:10.5px;vertical-align:top;}
      td.c{text-align:center;color:#6b7280;width:28px;} td.n{text-align:right;width:70px;} td.nt{font-size:9.5px;color:#6b7280;width:140px;}
      tr.vuota td{height:22px;}
      .nota{border:1px solid #fde68a;background:#fffbeb;border-radius:6px;padding:7px 10px;margin-bottom:10px;font-size:10.5px;}
      .firme{display:grid;grid-template-columns:1fr 1fr 1fr;gap:18px;margin-top:46px;page-break-inside:avoid;}
      .firma{border-top:1px solid #374151;padding-top:5px;font-size:9px;color:#6b7280;text-transform:uppercase;font-weight:700;}
      .firma span{display:block;text-transform:none;font-weight:400;margin-top:2px;}
      ${form.stato==="annullato"?`.foglio{position:relative;}.foglio::before{content:"ANNULLATO";position:absolute;top:42%;left:50%;transform:translate(-50%,-50%) rotate(-28deg);font-size:84px;font-weight:900;color:rgba(220,38,38,.13);pointer-events:none;}`:""}`;
    const corpo = testataHtml(orgInfo, {
      tipo:"Documento di Trasporto", numero, data: form.dataDocumento?`del ${dataLunga(form.dataDocumento)}`:"",
      badge:{ testo: stato.label, bg: stato.bg, col: stato.col },
      righe: [ codiceCommessa||projectName ? `<div class="ci-data">Commessa ${esc(codiceCommessa?`[${codiceCommessa}] `:"")}${esc(projectName)}</div>` : "",
        `<div class="ci-data" style="font-size:9px">D.P.R. 472/1996</div>` ],
    }) + `
      <div class="riquadri">
        <div class="box"><div class="box-t">Destinatario</div><h3>${esc(form.destinatarioNome)||"—"}</h3>
          ${form.destinatarioIndirizzo?`<p>${esc(form.destinatarioIndirizzo)}</p>`:""}
          ${form.destinatarioPiva||form.destinatarioCF?`<p>${[form.destinatarioPiva?`P.IVA ${esc(form.destinatarioPiva)}`:"",form.destinatarioCF?`C.F. ${esc(form.destinatarioCF)}`:""].filter(Boolean).join(" · ")}</p>`:""}</div>
        <div class="box"><div class="box-t">Luogo di destinazione</div><h3 style="font-size:12px">${esc(dest)||"—"}</h3></div>
      </div>
      <div class="trasporto">
        <div><span class="t-lbl">Causale del trasporto</span><span class="t-val">${esc(causale)||"—"}</span></div>
        <div><span class="t-lbl">Trasporto a cura del</span><span class="t-val">${esc(form.trasportoCura)}</span>${form.trasportoCura==="Vettore"&&form.vettoreNome?`<span style="display:block;font-size:9.5px">${esc(form.vettoreNome)}${form.vettoreIndirizzo?` — ${esc(form.vettoreIndirizzo)}`:""}</span>`:""}</div>
        <div><span class="t-lbl">Inizio trasporto</span><span class="t-val">${esc((form.dataInizioTrasporto||"").split("-").reverse().join("/"))} ${esc(form.oraInizioTrasporto)}</span></div>
        <div><span class="t-lbl">Colli · Peso</span><span class="t-val">${esc(form.nColli)||"—"}${form.peso?` · ${esc(form.peso)} kg`:""}</span></div>
        <div><span class="t-lbl">Aspetto dei beni</span><span class="t-val">${esc(form.aspettoEsteriore)||"—"}</span></div>
      </div>
      <table><thead><tr><th style="width:28px">#</th><th style="width:85px">Codice</th><th>Descrizione dei beni</th><th style="width:70px;text-align:right">Quantità</th><th style="width:45px">U.M.</th><th style="width:140px">Note</th></tr></thead><tbody>${righeHtml}</tbody></table>
      ${form.note?`<div class="nota"><strong>Note:</strong> ${esc(form.note)}</div>`:""}
      ${form.notesTrasporto?`<div class="nota" style="background:#f9fafb;border-color:#e5e7eb"><strong>Note trasporto:</strong> ${esc(form.notesTrasporto)}</div>`:""}
      <div class="firme">
        <div class="firma">Firma del mittente<span>${esc(orgInfo?.nome||orgInfo?.name||"")}</span></div>
        <div class="firma">Firma del vettore<span>${form.trasportoCura==="Vettore"?esc(form.vettoreNome):"&nbsp;"}</span></div>
        <div class="firma">Firma del destinatario<span>per ricevuta, timbro e firma</span></div>
      </div>`;
    const piede = piedeHtml(orgInfo, [numero||"DDT in bozza", `compilato da ${ddt?.compilatoreNome||currentUser?.displayName||""}`, `stampato il ${new Date().toLocaleDateString("it-IT")}`]);
    if(!apriDocumento({ titolo:`DDT ${numero||"bozza"}`, org:orgInfo, css, corpo, piede })) addToast("Il browser ha bloccato la finestra di stampa","error");
  };

  const isAnnullato=form.stato==="annullato";
  const isEmesso=form.stato==="emesso";
  const inp={padding:"8px 10px",border:"1px solid #e5e7eb",borderRadius:7,fontSize:13,fontFamily:"'Inter',sans-serif",outline:"none",width:"100%",boxSizing:"border-box"};
  const lbl={fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4};

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
      <div style={{background:"#fff",borderBottom:"1px solid #e5e7eb",padding:"12px 20px",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0,gap:8,flexWrap:"wrap"}}>
        <div>
          <div style={{fontSize:14,fontWeight:800,color:"#1e1b4b",display:"flex",alignItems:"center",gap:8}}>
            {ddt?.numero?<span style={{fontFamily:"monospace",color:"#7c3aed"}}>{ddt.numero}</span>:<span>{ddt?.id?"DDT in bozza":"Nuovo DDT"} <span style={{fontSize:11,fontWeight:600,color:"#9ca3af"}}>· il numero si assegna all'emissione</span></span>}
            <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,background:STATI_DDT[form.stato]?.bg,color:STATI_DDT[form.stato]?.col,fontWeight:700}}>{STATI_DDT[form.stato]?.label}</span>
          </div>
          {(projectName||clienteNome)&&<div style={{fontSize:11,color:"#9ca3af",marginTop:2}}>{codiceCommessa&&`[${codiceCommessa}] `}{projectName}{clienteNome&&` · ${clienteNome}`}</div>}
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          {commessaChiusa && <span style={{fontSize:11,fontWeight:700,color:"#dc2626"}}>🔒 Commessa chiusa</span>}
          <button onClick={stampaPDF} style={{display:"flex",alignItems:"center",gap:5,padding:"7px 14px",border:"1px solid #6b7280",borderRadius:8,background:"#fff",color:"#374151",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
            <Printer size={13}/> Stampa PDF
          </button>
          {!isAnnullato&&!isEmesso&&!commessaChiusa&&(
            <button onClick={()=>salva(false)} disabled={saving} style={{padding:"7px 14px",border:"1px solid #e5e7eb",borderRadius:8,background:"#fff",color:"#374151",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
              💾 Salva bozza
            </button>
          )}
          {!isAnnullato&&!isEmesso&&!commessaChiusa&&(
            <button onClick={()=>salva(true)} disabled={saving} style={{padding:"7px 18px",border:"none",borderRadius:8,background:"#059669",color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
              ✅ Emetti DDT
            </button>
          )}
          {isEmesso&&ddt?.id&&!commessaChiusa&&(
            <button onClick={annullaDDT} style={{padding:"7px 14px",border:"1px solid #dc2626",borderRadius:8,background:"#fee2e2",color:"#dc2626",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
              Annulla DDT
            </button>
          )}
          <button onClick={onClose} style={{padding:"7px 10px",border:"1px solid #e5e7eb",borderRadius:8,background:"#fff",cursor:"pointer"}}><X size={14}/></button>
        </div>
      </div>

      <div style={{flex:1,overflowY:"auto",padding:"16px 20px"}}>
        {isAnnullato&&<div style={{background:"#fee2e2",border:"1px solid #fca5a5",borderRadius:8,padding:"10px 14px",marginBottom:16,fontSize:13,fontWeight:700,color:"#dc2626"}}>⛔ DDT ANNULLATO — documento non più valido</div>}

        <Sez title="Testata documento" emoji="📄">
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
            <div><label style={lbl}>Data documento *</label><input type="date" style={inp} value={form.dataDocumento} onChange={e=>upd("dataDocumento",e.target.value)} disabled={isAnnullato}/></div>
            <div><label style={lbl}>Causale trasporto *</label>
              <select style={inp} value={form.causale} onChange={e=>upd("causale",e.target.value)} disabled={isAnnullato}>
                {CAUSALI.map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div><label style={lbl}>Stato</label>
              <div style={{padding:"8px 10px",background:STATI_DDT[form.stato]?.bg,color:STATI_DDT[form.stato]?.col,borderRadius:7,fontWeight:700,fontSize:13}}>{STATI_DDT[form.stato]?.label}</div>
            </div>
          </div>
          {form.causale==="Altro"&&<div style={{marginTop:10}}><label style={lbl}>Causale personalizzata</label><input style={inp} value={form.causalePersonalizzata} onChange={e=>upd("causalePersonalizzata",e.target.value)} placeholder="Descrivi la causale..." disabled={isAnnullato}/></div>}
        </Sez>

        <Sez title="Destinatario e destinazione" emoji="📍" colore="#2563eb" sfondo="#eff6ff">
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
            <div><label style={lbl}>Seleziona cliente</label>
              <select style={inp} value={form.clienteId} onChange={e=>upd("clienteId",e.target.value)} disabled={isAnnullato}>
                <option value="">— Seleziona da anagrafica —</option>
                {clienti.map(c=><option key={c.id} value={c.id}>{c.ragioneSociale}</option>)}
              </select>
            </div>
            <div><label style={lbl}>Ragione sociale *</label><input style={inp} value={form.destinatarioNome} onChange={e=>upd("destinatarioNome",e.target.value)} placeholder="Nome destinatario" disabled={isAnnullato}/></div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr",gap:12,marginBottom:12}}>
            <div><label style={lbl}>Indirizzo</label><input style={inp} value={form.destinatarioIndirizzo} onChange={e=>upd("destinatarioIndirizzo",e.target.value)} disabled={isAnnullato}/></div>
            <div><label style={lbl}>P.IVA</label><input style={inp} value={form.destinatarioPiva} onChange={e=>upd("destinatarioPiva",e.target.value)} disabled={isAnnullato}/></div>
            <div><label style={lbl}>Codice Fiscale</label><input style={inp} value={form.destinatarioCF} onChange={e=>upd("destinatarioCF",e.target.value)} disabled={isAnnullato}/></div>
          </div>
          <div>
            <SelettoreSede clienteId={form.clienteId||""} etichetta="Luogo di destinazione" disabled={isAnnullato}
              value={form.tipoDestinazione==="sede" ? form.sedeDestinazione : (form.tipoDestinazione==="manuale" && form.destinazioneManuale ? copiaLibera(form.destinazioneManuale) : null)}
              proposta={!ddt ? sedeCommessa : null}
              onChange={c=>setForm(f=>({...f, tipoDestinazione: c ? "sede" : "cliente", sedeDestinazione: c||null}))}/>
            <div style={{padding:"6px 10px",background:"#f0f9ff",borderRadius:7,fontSize:12,color:"#1d4ed8",fontWeight:600,marginTop:6}}>📍 {destinazioneEffettiva()||"—"}</div>
          </div>
        </Sez>

        <Sez title="Dati trasporto" emoji="🚚" colore="#059669" sfondo="#f0fdf4">
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:12,marginBottom:12}}>
            <div><label style={lbl}>Trasporto a cura di</label>
              <select style={inp} value={form.trasportoCura} onChange={e=>upd("trasportoCura",e.target.value)} disabled={isAnnullato}>
                {["Mittente","Destinatario","Vettore"].map(t=><option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div><label style={lbl}>Data inizio</label><input type="date" style={inp} value={form.dataInizioTrasporto} onChange={e=>upd("dataInizioTrasporto",e.target.value)} disabled={isAnnullato}/></div>
            <div><label style={lbl}>Ora inizio</label><input type="time" style={inp} value={form.oraInizioTrasporto} onChange={e=>upd("oraInizioTrasporto",e.target.value)} disabled={isAnnullato}/></div>
            <div><label style={lbl}>N° colli</label><input style={inp} type="number" min="0" value={form.nColli} onChange={e=>upd("nColli",e.target.value)} placeholder="0" disabled={isAnnullato}/></div>
          </div>
          {form.trasportoCura==="Vettore"&&(
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
              <div><label style={lbl}>Nome vettore</label><input style={inp} value={form.vettoreNome} onChange={e=>upd("vettoreNome",e.target.value)} disabled={isAnnullato}/></div>
              <div><label style={lbl}>Indirizzo vettore</label><input style={inp} value={form.vettoreIndirizzo} onChange={e=>upd("vettoreIndirizzo",e.target.value)} disabled={isAnnullato}/></div>
            </div>
          )}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
            <div><label style={lbl}>Peso (kg)</label><input style={inp} type="number" min="0" step="0.1" value={form.peso} onChange={e=>upd("peso",e.target.value)} placeholder="—" disabled={isAnnullato}/></div>
            <div><label style={lbl}>Aspetto esteriore beni</label><input style={inp} value={form.aspettoEsteriore} onChange={e=>upd("aspettoEsteriore",e.target.value)} placeholder="Es. Involto, Pallet..." disabled={isAnnullato}/></div>
            <div><label style={lbl}>Note trasporto</label><input style={inp} value={form.notesTrasporto} onChange={e=>upd("notesTrasporto",e.target.value)} placeholder="Annotazioni..." disabled={isAnnullato}/></div>
          </div>
        </Sez>

        <Sez title="Beni trasportati" emoji="📦" colore="#d97706" sfondo="#fffbeb">
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,marginBottom:10}}>
            <thead>
              <tr style={{background:"#1e1b4b"}}>
                <th style={{padding:"8px 10px",color:"#fff",fontSize:10,fontWeight:700,width:40,textAlign:"center"}}>#</th>
                <th style={{padding:"8px 10px",color:"#fff",fontSize:10,fontWeight:700,width:100}}>Codice</th>
                <th style={{padding:"8px 10px",color:"#fff",fontSize:10,fontWeight:700}}>Descrizione</th>
                <th style={{padding:"8px 10px",color:"#fff",fontSize:10,fontWeight:700,width:80,textAlign:"right"}}>Quantità</th>
                <th style={{padding:"8px 10px",color:"#fff",fontSize:10,fontWeight:700,width:80}}>U.M.</th>
                <th style={{padding:"8px 10px",color:"#fff",fontSize:10,fontWeight:700,width:140}}>Note</th>
                <th style={{padding:"8px 10px",color:"#fff",fontSize:10,fontWeight:700,width:60}}></th>
              </tr>
            </thead>
            <tbody>
              {righe.map((r,i)=>(
                <tr key={i} style={{background:i%2===0?"#fff":"#fafafa"}}>
                  <td style={{padding:"6px 10px",textAlign:"center",color:"#9ca3af",fontWeight:700}}>{i+1}</td>
                  <td style={{padding:"4px 6px"}}><input value={r.codice} onChange={e=>updRiga(i,"codice",e.target.value)} disabled={isAnnullato} style={{...inp,padding:"5px 7px",fontSize:12}} placeholder="Codice"/></td>
                  <td style={{padding:"4px 6px"}}><input value={r.descrizione} onChange={e=>updRiga(i,"descrizione",e.target.value)} disabled={isAnnullato} style={{...inp,padding:"5px 7px",fontSize:12}} placeholder="Descrizione bene o materiale..."/></td>
                  <td style={{padding:"4px 6px"}}><input type="number" min="0" step="0.01" value={r.quantita} onChange={e=>updRiga(i,"quantita",e.target.value)} disabled={isAnnullato} style={{...inp,padding:"5px 7px",fontSize:12,textAlign:"right"}} placeholder="0"/></td>
                  <td style={{padding:"4px 6px"}}><select value={r.um} onChange={e=>updRiga(i,"um",e.target.value)} disabled={isAnnullato} style={{...inp,padding:"5px 7px",fontSize:12}}>{UM_OPTIONS.map(u=><option key={u} value={u}>{u}</option>)}</select></td>
                  <td style={{padding:"4px 6px"}}><input value={r.note} onChange={e=>updRiga(i,"note",e.target.value)} disabled={isAnnullato} style={{...inp,padding:"5px 7px",fontSize:12}} placeholder="Note..."/></td>
                  <td style={{padding:"4px 6px",textAlign:"center"}}>
                    <div style={{display:"flex",gap:4,justifyContent:"center"}}>
                      <button onClick={()=>duplicaRiga(i)} disabled={isAnnullato} title="Duplica" style={{background:"none",border:"none",cursor:"pointer",color:"#9ca3af",padding:2}}><Copy size={12}/></button>
                      <button onClick={()=>removeRiga(i)} disabled={isAnnullato||righe.length<=1} style={{background:"none",border:"none",cursor:"pointer",color:"#fca5a5",padding:2}}><Trash2 size={12}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!isAnnullato&&<button onClick={addRiga} style={{display:"flex",alignItems:"center",gap:6,padding:"8px 14px",border:"1px dashed #d97706",borderRadius:8,background:"#fffbeb",color:"#d97706",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}><Plus size={13}/> Aggiungi riga</button>}
        </Sez>

        <Sez title="Note e riepilogo" emoji="📝">
          <div><label style={lbl}>Note generali</label>
            <textarea value={form.note} onChange={e=>upd("note",e.target.value)} disabled={isAnnullato} placeholder="Annotazioni aggiuntive..." style={{...inp,minHeight:70,resize:"vertical"}}/>
          </div>
          <div style={{marginTop:12,display:"flex",gap:12,flexWrap:"wrap"}}>
            {[{l:"N° righe",v:righe.filter(r=>r.descrizione).length},{l:"N° colli",v:form.nColli||"—"},...(form.peso?[{l:"Peso",v:`${form.peso} kg`}]:[])].map(f=>(
              <div key={f.l} style={{background:"#f9fafb",border:"1px solid #e5e7eb",borderRadius:8,padding:"8px 14px"}}>
                <div style={{fontSize:10,color:"#9ca3af",fontWeight:700,textTransform:"uppercase"}}>{f.l}</div>
                <div style={{fontSize:18,fontWeight:800,color:"#374151"}}>{f.v}</div>
              </div>
            ))}
          </div>
        </Sez>
      </div>
    </div>
  );
}

// Usata nella commessa (elenco dei suoi DDT) e dal Registro DDT, che apre direttamente un
// DDT esistente (apriDDT) o un nuovo DDT (nuovo) e, alla chiusura, torna al registro (onEsci).
export default function DDTTab({ projectId, projectName, codiceCommessa, clienteNome, clienteId, orgId, commessaChiusa=false, apriDDT=null, nuovo=false, onEsci=null }) {
  const { addToast } = useToast();
  const [ddtList, setDdtList] = useState([]);
  const [orgInfo, setOrgInfo] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editDDT, setEditDDT] = useState(null);

  useEffect(()=>{
    if(!projectId||!orgId) return;
    return onSnapshot(
      query(collection(db,"ddt"),where("projectId","==",projectId),where("orgId","==",orgId)),
      snap=>setDdtList(snap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(b.createdAt?.seconds||0)-(a.createdAt?.seconds||0)))
    );
  },[projectId,orgId]);

  useEffect(()=>{
    if(!orgId) return;
    getDoc(doc(db,"organizations",orgId)).then(snap=>{if(snap.exists())setOrgInfo(snap.data());});
  },[orgId]);

  const elimina=async id=>{
    if(commessaChiusa) return;
    if(!await confirm("Eliminare questo DDT?",{type:"danger"})) return;
    await deleteDoc(doc(db,"ddt",id));
    addToast("Eliminato","info");
  };

  if(onEsci && (apriDDT || nuovo)){
    return <DDTForm ddt={apriDDT} projectId={projectId} projectName={projectName}
      codiceCommessa={codiceCommessa} clienteNome={clienteNome} clienteId={clienteId}
      orgId={orgId} orgInfo={orgInfo} commessaChiusa={commessaChiusa} onClose={onEsci}/>;
  }

  if(showForm||editDDT){
    return <DDTForm ddt={editDDT} projectId={projectId} projectName={projectName}
      codiceCommessa={codiceCommessa} clienteNome={clienteNome} clienteId={clienteId}
      orgId={orgId} orgInfo={orgInfo} commessaChiusa={commessaChiusa}
      onClose={()=>{setShowForm(false);setEditDDT(null);}}/>;
  }

  return (
    <div style={{padding:20}}>
      <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:16}}>
        <h3 style={{fontSize:15,fontWeight:700,color:"#1e1b4b",margin:0,display:"flex",alignItems:"center",gap:6}}><FileText size={15}/> DDT — Documenti di Trasporto</h3>
        {!commessaChiusa && (
          <button onClick={()=>setShowForm(true)} className="btn btn-primary" style={{display:"flex",alignItems:"center",gap:5,padding:"5px 12px",fontSize:12}}>
            <Plus size={13}/> Nuovo DDT
          </button>
        )}
        {commessaChiusa && <span style={{fontSize:11,fontWeight:700,color:"#dc2626"}}>🔒 Commessa chiusa — sola consultazione</span>}
        <span style={{fontSize:11,color:"#9ca3af",marginLeft:"auto"}}>{ddtList.length} documento/i</span>
      </div>
      {ddtList.length===0?(
        <div style={{textAlign:"center",padding:60,color:"#9ca3af"}}>
          <FileText size={48} style={{opacity:.15,marginBottom:12}}/>
          <div style={{fontSize:14,fontWeight:600}}>Nessun DDT</div>
          <div style={{fontSize:12,marginTop:4}}>Crea il primo documento di trasporto</div>
        </div>
      ):ddtList.map((d,i)=>{
        const stato=STATI_DDT[d.stato]||STATI_DDT.bozza;
        return (
          <div key={d.id} style={{border:"1px solid #e5e7eb",borderRadius:10,padding:"12px 16px",marginBottom:10,background:"#fff",display:"flex",alignItems:"center",gap:12,cursor:"pointer"}} onClick={()=>setEditDDT(d)}>
            <FileText size={20} color="#7c3aed" style={{flexShrink:0}}/>
            <div style={{flex:1}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                {d.numero?<span style={{fontFamily:"monospace",fontWeight:800,color:"#7c3aed",fontSize:14}}>{d.numero}</span>:<span style={{fontWeight:700,color:"#9ca3af",fontSize:13}}>Bozza — numero all'emissione</span>}
                <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,background:stato.bg,color:stato.col,fontWeight:700}}>{stato.label}</span>
              </div>
              <div style={{fontSize:11,color:"#9ca3af",marginTop:2}}>{d.dataDocumento?.split("-").reverse().join("/")} · {d.destinatarioNome} · {d.causale}</div>
            </div>
            <div style={{fontSize:11,color:"#6b7280"}}>{d.compilatoreNome}</div>
            {d.stato==="bozza"&&!commessaChiusa&&<button onClick={e=>{e.stopPropagation();elimina(d.id);}} style={{background:"none",border:"none",cursor:"pointer",color:"#fca5a5",padding:4}}><Trash2 size={14}/></button>}
          </div>
        );
      })}
    </div>
  );
}
